/**
 * ぴったりの商品をさがす（c-search 専用）
 *
 * 流れ: 入力変更 → 検索 URL 組み立て → 要約・件数・「商品をさがす」href を更新（件数は fetch）。
 * 設定: sections/c-search.liquid の #c-fit-filter-config-*（Liquid と JS のキー名を揃えること）。
 *
 * ファイル構成（上から順に読むと追いやすいです）
 *   1) 定数・グローバル補助（DOMReady / 設定 parse / PC-SP ミラー）
 *   2) メタ価格スライダー・件数 HTML パース・検索 URL 組み立て（セクション非依存の純関数に近いもの）
 *   3) cFitRegisterSectionChrome / cFitRegisterSectionFilterEvents … クリック・リサイズ・委譲リスナ
 *   4) window.cFitSearchSectionInit … セクション1つ分の状態と「deps」束ね
 *   5) ページ末尾 … JSON スキャンして Init を起動
 *
 * フィルタ UI は全ブレークポイントで modal-content を正とする（PC 用 .c-filter-content は未使用）。
 * 将来 PC パネルを復活させた場合のみ、幅で「正」の枝を切り替え cFitMirrorFilterItemAcrossLayouts で同期。
 */
(function () {
  // --- モジュール定数（ブレークポイントは CSS の hidden-pocket / hidden-desk と揃える） ---
  var FIT_COUNT_CACHE_TTL_MS = 4000;
  var FIT_COUNT_DEBOUNCE_MS = 250;
  /** 旧 PC ドロップパネル用の最小幅（px）。現行はモーダルのみのため aria 同期などにのみ参照。 */
  var FIT_PC_MIN_WIDTH = 1200;
  /** matchMedia 用（リサイズ時のミラー用。入力の正は常に modal-content）。 */
  var FIT_NARROW_MQL = '(max-width: 1199px)';

  var DEFAULT_FIT_FILTER_CONFIG = Object.freeze({
    hierarchicalMerges: [],
    singleParamKeys: [],
    countUnavailable: '',
  });

  /**
   * 同一セクション DOM の二重初期化を防ぐ（テーマエディタのプレビュー更新などでスクリプトが再実行されることがある）。
   * WeakSet はセクション要素が DOM から消えたら参照も捨てられるのでリークしにくい。
   */
  var cFitInitializedSectionRoots = new WeakSet();

  /**
   * defer スクリプトは多くの場合すでに interactive/complete なので即実行。
   * まれに早すぎる読み込みでは DOMContentLoaded を待つ。
   * @param {function} fn DOM 準備完了後に1回だけ実行するコールバック
   */
  function cFitDomReady(fn) {
    if (document.readyState === 'loading') {
      // まだパース中なら DOM 構築完了まで待つ（{ once: true } で1回限り）
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /**
   * CSS 属性セレクタ用に値をエスケープ（param / value に記号が含まれる場合の対策）
   * @param {string} s エスケープする文字列
   * @returns {string}
   */
  function cFitEscapeAttr(s) {
    return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(s)) : String(s).replace(/["\\]/g, '\\$&');
  }

  /**
   * #c-fit-filter-config-{sectionId} を読み、壊れていればデフォルト
   * @param {string} sectionId Shopify セクション ID
   * @returns {{ hierarchicalMerges: object[], singleParamKeys: string[], countUnavailable: string }}
   */
  function cFitParseFilterConfigScript(sectionId) {
    var el = document.getElementById('c-fit-filter-config-' + sectionId);
    if (!el) return DEFAULT_FIT_FILTER_CONFIG;
    try {
      var parsed = JSON.parse(el.textContent || '{}');
      return {
        hierarchicalMerges: Array.isArray(parsed.hierarchicalMerges) ? parsed.hierarchicalMerges : [],
        singleParamKeys: Array.isArray(parsed.singleParamKeys) ? parsed.singleParamKeys : [],
        countUnavailable: typeof parsed.countUnavailable === 'string' ? parsed.countUnavailable : '',
      };
    } catch (_) {
      return DEFAULT_FIT_FILTER_CONFIG;
    }
  }

  /**
   * 件数取得などで使う HTML 取得用 fetch（AbortSignal のみ差し替え）
   * @param {string} pathnamePlusSearch 例: /search?q=*&…
   * @param {AbortSignal|undefined} signal 中止用（省略可）
   * @returns {Promise<Response>}
   */
  function cFitFetchHtml(pathnamePlusSearch, signal) {
    return fetch(pathnamePlusSearch, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: signal,
    });
  }

  /**
   * PC/SP 境界を跨いだとき、直前に有効だった側（モーダル or PC パネル）の入力を反対側へ複製する。
   * 二重 DOM だと要約・URL が片側だけ更新されるため、matchMedia('change') から呼ぶ。
   * @param visibleWasNarrow true = 直前は狭幅（モーダル側が有効）
   *
   * 【なぜ必要か】ユーザーがスマホ幅で選んだあと画面を広げた場合、PC 用パネルの checkbox は
   * まだ未操作のままなので、この関数で状態をコピーしないと要約・検索 URL が空のままになる。
   * @param {Element|null} filterItem .c-filter-item
   * @param {boolean} visibleWasNarrow 直前が狭幅（モーダル側が正）なら true
   * @param {function(Element, string, string): void} syncMetaSliderGroupIn メタ価格帯の同期（cFitSyncMetaSliderGroupIn 相当）
   */
  function cFitMirrorFilterItemAcrossLayouts(filterItem, visibleWasNarrow, syncMetaSliderGroupIn) {
    if (!filterItem) return;
    var sourceRoot = visibleWasNarrow
      ? filterItem.querySelector('modal-content')
      : filterItem.querySelector('.c-filter-content');
    var targetRoot = visibleWasNarrow
      ? filterItem.querySelector('.c-filter-content')
      : filterItem.querySelector('modal-content');
    if (!sourceRoot || !targetRoot) return;

    // param + value が一致するペアを探して checked をコピー（PC とモーダルで id は違うが data-filter-param は同じ設計）
    sourceRoot.querySelectorAll('input[data-filter-param]').forEach(function (srcInp) {
      if (!(srcInp instanceof HTMLInputElement)) return;
      if (srcInp.type !== 'checkbox' && srcInp.type !== 'radio') return;
      var param = srcInp.dataset.filterParam;
      if (!param) return;
      var sel =
        'input[data-filter-param="' +
        cFitEscapeAttr(param) +
        '"][value="' +
        cFitEscapeAttr(srcInp.value) +
        '"]';
      var tgtInp = targetRoot.querySelector(sel);
      if (!(tgtInp instanceof HTMLInputElement)) return;
      if (tgtInp.type === srcInp.type) tgtInp.checked = srcInp.checked;
    });

    var seenGids = new Set();
    // 価格帯スライダーは同じ data-c-fit-meta-slider-group を PC/モーダルで共有するので、range の値を同期
    sourceRoot.querySelectorAll('[data-c-fit-meta-slider-group]').forEach(function (wrap) {
      var gid = wrap.getAttribute('data-c-fit-meta-slider-group');
      if (!gid || seenGids.has(gid)) return;
      seenGids.add(gid);
      var r = wrap.querySelector('.c-price-filter__range--meta');
      if (r) syncMetaSliderGroupIn(filterItem, gid, r.value);
    });
  }

  // --------------------------------------------------------------------------
  // 以降: セクションに依存しない（または引数で渡す）ヘルパー
  // --------------------------------------------------------------------------

  /**
   * Liquid の data-* kebab を input.dataset の camelCase 名に直す。
   * hierarchicalMerges の datasetSuffixKebab と datasetKey の両方に対応。
   * @param {string} kebab 例: parent-label
   * @returns {string} 例: parentLabel
   */
  function cFitDatasetSuffixKebabToCamel(kebab) {
    if (!kebab || typeof kebab !== 'string') return '';
    return kebab.replace(/-([a-z])/gi, function (_, c) {
      return c.toUpperCase();
    });
  }

  /**
   * hierarchicalMerges の1件から、子 input の dataset 参照用 camelCase 名を得る。
   * Liquid は datasetSuffixKebab（kebab）か datasetKey（camel）のどちらかを出す想定。
   * @param {object|null|undefined} m merge 定義1件
   * @returns {string}
   */
  function cFitHierarchicalMergeDatasetCamel(m) {
    if (!m) return '';
    if (m.datasetSuffixKebab) return cFitDatasetSuffixKebabToCamel(m.datasetSuffixKebab);
    return typeof m.datasetKey === 'string' ? m.datasetKey : '';
  }

  /**
   * .c-price-filter--meta-slider 内の JSON script から帯一覧 [{v,l},...] を取り出す（壊れていれば []）
   * @param {Element|null} wrap メタ価格帯のラッパー
   * @returns {object[]}
   */
  function cFitMetaBandsFromWrap(wrap) {
    if (!wrap) return [];
    var script = wrap.querySelector('script.c-fit-meta-bands-data[type="application/json"]');
    if (!script) return [];
    try {
      var data = JSON.parse(script.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (_) {
      return [];
    }
  }

  /**
   * メタ価格帯スライダーのインデックスを反映する。
   * hidden の value・プログレスバー・ハンドルラベル・ARIA をまとめて更新。
   * @param {Element} wrap .c-price-filter--meta-slider
   * @param {string|number} rawIdx 0=未選択、1..n=帯
   */
  function cFitApplyMetaSliderIndex(wrap, rawIdx) {
    var bands = cFitMetaBandsFromWrap(wrap);
    var n = bands.length;
    var range = wrap.querySelector('.c-price-filter__range--meta');
    var hidden = wrap.querySelector('.c-fit-meta-band-filter-value');
    var progress = wrap.querySelector('.c-price-filter__progress');
    var labelEl = wrap.querySelector('.c-price-filter__handle-label--meta');
    if (!range || !hidden || !progress) return;
    var i = parseInt(String(rawIdx), 10);
    if (Number.isNaN(i)) i = 0;
    i = Math.max(0, Math.min(n, i));
    range.value = String(i);
    var noneLabel = wrap.dataset.metaNoneLabel || '';
    if (i === 0 || n === 0) {
      hidden.value = '';
      delete hidden.dataset.cFitMetaBandLabel;
    } else {
      var b = bands[i - 1];
      hidden.value = b && b.v != null ? String(b.v) : '';
      hidden.dataset.cFitMetaBandLabel = b && b.l != null ? String(b.l) : hidden.value;
    }
    if (n > 0) {
      var pct = (i / n) * 100;
      progress.style.left = '0%';
      progress.style.width = Math.max(0, Math.min(100, pct)) + '%';
    } else {
      progress.style.width = '0%';
    }
    if (labelEl) {
      if (i === 0 || n === 0) {
        labelEl.textContent = noneLabel || '指定なし';
      } else {
        labelEl.textContent = (bands[i - 1] && (bands[i - 1].l || bands[i - 1].v)) || '';
      }
      var pct2 = n > 0 ? (i / n) * 100 : 0;
      labelEl.style.left = pct2 + '%';
      labelEl.classList.toggle('is-edge-start', pct2 <= 6);
      labelEl.classList.toggle('is-edge-end', pct2 >= 94);
    }
    if (range.hasAttribute('aria-valuenow')) range.setAttribute('aria-valuenow', String(i));
    if (range.hasAttribute('aria-valuemax')) range.setAttribute('aria-valuemax', String(n));
  }

  /**
   * 同一 data-c-fit-meta-slider-group を持つ wrap が PC/モーダルに複数あっても、まとめて同じインデックスに揃える。
   * @param {Element|null} container .c-filter-item またはセクションルート
   * @param {string|number} groupId data-c-fit-meta-slider-group の値
   * @param {string|number} rawIdx cFitApplyMetaSliderIndex と同じ
   */
  function cFitSyncMetaSliderGroupIn(container, groupId, rawIdx) {
    if (!container || groupId == null || groupId === '') return;
    var gid = String(groupId);
    container.querySelectorAll('[data-c-fit-meta-slider-group="' + CSS.escape(gid) + '"]').forEach(function (w) {
      cFitApplyMetaSliderIndex(w, rawIdx);
    });
  }

  /**
   * 検索結果 HTML 文字列から件数を推定する（専用 section 優先、失敗時は DOM のテキストから）。
   * Bot チャレンジ等のページは null（件数不明）、空状態は 0。
   * @param {string} html 取得した HTML 全文
   * @returns {number|null|undefined} 件数。判定不能は null、パース失敗時は undefined のことあり
   */
  function cFitParseSearchProductCount(html) {
    var lower = html.toLowerCase();
    if (
      html.includes('接続の確認') ||
      lower.includes('challenge-platform') ||
      lower.includes('cf-challenge') ||
      lower.includes('checking your browser') ||
      lower.includes('just a moment')
    ) {
      return null;
    }

    var doc = new DOMParser().parseFromString(html, 'text/html');

    var countPreview = doc.querySelector('.c-fit-count-preview[data-c-fit-results-count]');
    if (countPreview) {
      var raw = countPreview.getAttribute('data-c-fit-results-count') || '';
      var parsed = parseInt(raw, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }

    if (doc.querySelector('.product-facet #facet-main .empty-state')) return 0;
    if (doc.querySelector('.main-search__results .empty-state.empty-state--bottom-only')) return 0;
    if (doc.querySelector('.container > .empty-state.empty-state--bottom-only')) return 0;

    var catResult = doc.querySelector('.main-search__category-result[data-label]');
    if (catResult) {
      var dl = catResult.getAttribute('data-label') || '';
      var mLabel = dl.match(/[(（]\s*([0-9][0-9,]*)\s*[)）]/);
      if (mLabel && mLabel[1]) return parseInt(mLabel[1].replace(/,/g, ''), 10);
    }

    var countNode = doc.querySelector('.product-facet__meta-bar-item--count');
    if (countNode) {
      var text = countNode.textContent || '';
      var m = text.match(/([0-9][0-9,]*)/);
      if (m && m[1]) return parseInt(m[1].replace(/,/g, ''), 10);
    }

    return null;
  }

  /**
   * .c-filter-item の直下子から PC 用ドロップパネル（.c-filter-content）を1つ返す。
   * querySelector だとネストした .c-filter-content を誤爆しうるため、子要素を順に見る。
   * @param {Element|null} filterItem
   * @returns {HTMLElement|null}
   */
  function cFitGetPcFilterContentPanel(filterItem) {
    if (!filterItem) return null;
    for (var i = 0; i < filterItem.children.length; i++) {
      var el = filterItem.children[i];
      if (el instanceof HTMLElement && el.classList.contains('c-filter-content')) {
        return el;
      }
    }
    return null;
  }

  /**
   * 階層: 子チェック ON 時に、同スコープの親ラジオを dataset に合わせて ON。
   * @param {HTMLInputElement} checkbox 子側のチェックボックス
   * @param {object[]} hierarchicalMerges Liquid の階層マージ定義
   */
  function cFitSyncHierarchicalParentRadio(checkbox, hierarchicalMerges) {
    var filterItem = checkbox.closest('.c-filter-item');
    if (!filterItem) return;
    var pcPanel = cFitGetPcFilterContentPanel(filterItem);
    var scope = checkbox.closest('.c-modal-scroll') || pcPanel || filterItem;
    var hMergesCb = hierarchicalMerges || [];
    var fp = checkbox.dataset.filterParam;
    for (var mi = 0; mi < hMergesCb.length; mi++) {
      var m = hMergesCb[mi];
      var dk = cFitHierarchicalMergeDatasetCamel(m);
      if (!m || !m.childParam || !m.parentParam || !dk) continue;
      if (fp !== m.childParam) continue;
      var parentVal = checkbox.dataset[dk];
      if (!parentVal || !checkbox.checked) continue;
      var radio = scope.querySelector(
        'input[type="radio"][data-filter-param="' +
          CSS.escape(m.parentParam) +
          '"][value="' +
          CSS.escape(parentVal) +
          '"]'
      );
      if (radio instanceof HTMLInputElement) radio.checked = true;
    }
  }

  /**
   * 階層: その行の子チェックが1つも残っていなければ親ラジオも OFF。
   * 子だけ外しても親ラジオが残り、アイコンが :has(:checked) のままになるのを防ぐ（子が0件の行は何もしない）。
   * @param {HTMLInputElement} checkbox 変更された子チェックボックス
   * @param {object[]} hierarchicalMerges Liquid の階層マージ定義
   */
  function cFitClearHierarchicalParentIfNoChildren(checkbox, hierarchicalMerges) {
    if (!(checkbox instanceof HTMLInputElement) || checkbox.type !== 'checkbox') return;
    var fp = checkbox.dataset.filterParam;
    var merge = null;
    var hMergesCb = hierarchicalMerges || [];
    for (var mi = 0; mi < hMergesCb.length; mi++) {
      var m = hMergesCb[mi];
      if (m && m.childParam === fp) {
        merge = m;
        break;
      }
    }
    if (!merge || !merge.parentParam) return;

    var row = checkbox.closest('.c-filter-content__item');
    if (!row) return;
    var area = row.querySelector('.c-filter-content__item-area');
    if (!area) return;

    var childParamEsc = CSS.escape(merge.childParam);
    var childBoxes = area.querySelectorAll('input[type="checkbox"][data-filter-param="' + childParamEsc + '"]');
    if (childBoxes.length === 0) return;

    var anyChecked = false;
    for (var ci = 0; ci < childBoxes.length; ci++) {
      if (childBoxes[ci].checked) {
        anyChecked = true;
        break;
      }
    }
    if (anyChecked) return;

    var radio = row.querySelector(
      'input[type="radio"][data-filter-param="' + CSS.escape(merge.parentParam) + '"]'
    );
    if (radio instanceof HTMLInputElement) radio.checked = false;
  }

  /**
   * 階層: 行タイトル／アイコン／トグルで行を選んだとき、見出し内の隠し親ラジオを ON。
   * CSS は :has(input[data-filter-param]:checked) 前提のため、クリックだけではラジオが付かずオレンジにならない問題を防ぐ。
   * 同一 name のため他行の親ラジオはブラウザが自動で OFF。change で要約・件数も更新。
   * @param {Element|null} title .c-filter-content__item-title など
   */
  function cFitCheckHierarchicalParentRadioFromTitle(title) {
    if (!title) return;
    var radio = title.querySelector('input.c-input[type="radio"][data-filter-param]');
    if (!(radio instanceof HTMLInputElement)) return;
    var wasChecked = radio.checked;
    radio.checked = true;
    if (!wasChecked) {
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * チェック・ラジオの選択 + 価格帯 hidden + hierarchicalMerges から検索 URL を組み立てる。
   * getCheckboxRadios / getPriceBandHiddens は「現在の表示モードで正しい枝」のみ返すこと。
   * @param {object} parts
   * @param {string} parts.searchBasePath
   * @param {object} parts.defaultQuery 例: { q: '*', type: 'product' }
   * @param {Set<string>} parts.singleParams 同一キーは最後の値のみ
   * @param {object[]} [parts.hierarchicalMerges]
   * @param {function(): HTMLInputElement[]} parts.getCheckboxRadios チェック済みの filter 用 input
   * @param {function(): HTMLInputElement[]} parts.getPriceBandHiddens 価格帯 hidden（値ありのみ）
   * @returns {URL}
   */
  function cFitBuildSearchUrlFromParts(parts) {
    var url = new URL(parts.searchBasePath, window.location.origin);
    Object.entries(parts.defaultQuery).forEach(function (kv) {
      url.searchParams.set(kv[0], kv[1]);
    });

    var inputs = parts.getCheckboxRadios();
    var collected = new Map();

    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (!input.checked) continue;
      var param = input.dataset.filterParam;
      var value = input.value;
      if (!param || !value) continue;
      if (!collected.has(param)) collected.set(param, []);
      collected.get(param).push(value);
    }

    var hiddens = parts.getPriceBandHiddens();
    for (var h = 0; h < hiddens.length; h++) {
      var el = hiddens[h];
      var p = el.dataset.filterParam;
      var v = (el.value || '').trim();
      if (!p || !v) continue;
      if (!collected.has(p)) collected.set(p, []);
      collected.get(p).push(v);
    }

    var hMerges = parts.hierarchicalMerges || [];
    for (var m = 0; m < hMerges.length; m++) {
      var merge = hMerges[m];
      var dk = cFitHierarchicalMergeDatasetCamel(merge);
      if (merge && merge.childParam && merge.parentParam && dk) {
        cFitMergeParentIntoCollected(inputs, collected, merge.childParam, merge.parentParam, dk);
      }
    }

    var singleParams = parts.singleParams;
    collected.forEach(function (values, param) {
      if (singleParams.has(param)) {
        url.searchParams.set(param, values[values.length - 1]);
      } else {
        for (var k = 0; k < values.length; k++) {
          url.searchParams.append(param, values[k]);
        }
      }
    });

    return url;
  }

  /**
   * 階層フィルタ: 子チェックに紐づく親の値を collected の parentParam にマージ（URL 用 Map）。
   * チェック済み子の dataset[datasetCamel] が親の filter 値になる。
   * @param {HTMLInputElement[]} inputs チェック済みを含む入力一覧
   * @param {Map<string, string[]>} collected param → 値の配列
   * @param {string} childParam 子の data-filter-param
   * @param {string} parentParam 親の data-filter-param
   * @param {string} datasetCamel 子の dataset 上の親値キー（camelCase）
   */
  function cFitMergeParentIntoCollected(inputs, collected, childParam, parentParam, datasetCamel) {
    var parents = new Set();
    for (var j = 0; j < inputs.length; j++) {
      var inp = inputs[j];
      if (!inp.checked || inp.dataset.filterParam !== childParam) continue;
      var pv = inp.dataset[datasetCamel];
      if (pv) parents.add(pv);
    }
    var existing = collected.get(parentParam) || [];
    if (parents.size === 0 && existing.length === 0) return;
    var merged = Array.from(new Set(existing.concat(Array.from(parents))));
    collected.set(parentParam, merged);
  }

  /**
   * アコーディオン・モーダル開閉・PC パネル・matchMedia（セクション外 document へのリスナ含む）
   * @param {object} deps cFitSectionDeps 互換（root / sectionQueryAll / revertFilterItemDraft / beginFilterItemSession / syncPcFilterRowAria / syncMetaSliderGroupIn / setFilterRowAriaExpanded / FIT_PC_MIN_WIDTH / FIT_NARROW_MQL など）
   */
  function cFitRegisterSectionChrome(deps) {
    deps.sectionQueryAll('.c-filter-toggle').forEach(function (toggle) {
      /**
       * メイン「ぴったり」フィルタ全体のアコーディオン開閉
       */
      function toggleMainFilter() {
        toggle.classList.toggle('is-active');
        toggle.setAttribute('aria-expanded', toggle.classList.contains('is-active') ? 'true' : 'false');
      }
      toggle.addEventListener('click', toggleMainFilter);
      toggle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleMainFilter();
        }
      });
    });

    deps.sectionQueryAll('.c-filter-item__toggle').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var filterItem = this.closest('.c-filter-item');
        if (!filterItem) return;

        var modal = filterItem.querySelector('modal-content');
        if (!modal) return;

        if (modal.hasAttribute('open')) {
          return;
        }

        if (deps.root) {
          deps.root.querySelectorAll('.c-filter-item modal-content[open]').forEach(function (m) {
            if (m === modal) return;
            var fi = m.closest('.c-filter-item');
            if (fi) {
              deps.revertFilterItemDraft(fi);
              deps.setFilterRowAriaExpanded(fi, false);
            }
            m.open = false;
          });
        }

        deps.beginFilterItemSession(filterItem);
        modal.setAttribute('open', '');
        deps.setFilterRowAriaExpanded(filterItem, true);
      });
    });

    deps.sectionQueryAll('.c-filter-close').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var filterItem = this.closest('.c-filter-item');
        if (filterItem) {
          deps.revertFilterItemDraft(filterItem);
          deps.setFilterRowAriaExpanded(filterItem, false);
        }
        var parent = this.closest('.c-filter-content');
        if (parent) {
          parent.classList.remove('is-active');
        }
        deps.syncPcFilterRowAria();
      });
    });

    // テーマの <modal-content> 閉鎖時に発火するカスタムイベント。セクション外の document で受け、ドラフト破棄と aria-expanded を戻す。
    document.addEventListener('openable-element:close', function (e) {
      var m = e.target;
      if (!(m instanceof HTMLElement) || m.tagName !== 'MODAL-CONTENT') return;
      if (!deps.root || !deps.root.contains(m)) return;
      var filterItem = m.closest('.c-filter-item');
      if (!filterItem) return;
      deps.revertFilterItemDraft(filterItem);
      deps.setFilterRowAriaExpanded(filterItem, false);
    });

    // PC 階層: 左サイドバーの見出しクリックで .c-filter-content__item の is-active を1つに（モーダル内は下のリスナに任せる）
    document.addEventListener('click', function (e) {
      var title = e.target.closest('.c-filter-content__sidebar .c-filter-content__item-title');
      if (!title || !deps.root || !deps.root.contains(title)) return;
      if (title.closest('.c-modal-body')) return;

      var item = title.closest('.c-filter-content__item');
      var sidebar = title.closest('.c-filter-content__sidebar');
      if (!item || !sidebar) return;

      sidebar.querySelectorAll('.c-filter-content__item').forEach(function (el) {
        el.classList.remove('is-active');
      });
      item.classList.add('is-active');
      cFitCheckHierarchicalParentRadioFromTitle(title);
    });

    // 狭幅モーダル内: 階層リスト（.c-fit-mo-hierarchical__list-scroll）の見出しアコーディオンをトグル
    document.addEventListener('click', function (e) {
      var title = e.target.closest('.c-modal-body .c-filter-content__sidebar .c-filter-content__item-title');
      if (!title || !deps.root || !deps.root.contains(title)) return;
      if (!title.closest('.c-fit-mo-hierarchical__list-scroll')) return;
      var row = title.closest('.c-filter-content__item');
      if (!row) return;

      var expanded = title.classList.contains('is-active');
      if (expanded) {
        title.classList.remove('is-active');
        row.classList.remove('is-active');
      } else {
        title.classList.add('is-active');
        row.classList.add('is-active');
        cFitCheckHierarchicalParentRadioFromTitle(title);
      }
    });

    // FIT_NARROW_MQL（1200px 未満 = narrow）の一致が変わったときに発火。document ではなく MediaQueryList に登録する。
    var mmNarrow = window.matchMedia(deps.FIT_NARROW_MQL);
    /**
     * PC/モバイル境界を跨いだとき: 二重 DOM をミラーし要約・件数を更新
     * @param {MediaQueryListEvent} e matchMedia の change
     */
    function onViewportFitBreakpointChange(e) {
      if (!deps.root) return;
      var nowNarrow = e.matches;
      var wasNarrow = !nowNarrow;
      deps.sectionQueryAll('.c-filter-item').forEach(function (item) {
        cFitMirrorFilterItemAcrossLayouts(item, wasNarrow, deps.syncMetaSliderGroupIn);
      });
      deps.updateSummary();
      deps.updateCount();
    }
    // MediaQueryList#addEventListener('change') はモダンブラウザ。古い Safari 等は従来の addListener のみ。
    if (mmNarrow.addEventListener) {
      mmNarrow.addEventListener('change', onViewportFitBreakpointChange);
    } else if (mmNarrow.addListener) {
      mmNarrow.addListener(onViewportFitBreakpointChange);
    }
  }

  /**
   * DOMContentLoaded 後: スライダー初期化・change/input 委譲・フッタのクリア/保存
   * @param {object} deps cFitSectionDeps（root / sectionQueryAll / updateSummary / updateCount / hierarchicalMerges / syncMetaSliderGroupIn / applyMetaSliderIndex など）
   */
  function cFitRegisterSectionFilterEvents(deps) {
    if (deps.root) {
      // セクションルートへの委譲: メタ価格帯 range の input のみ拾い、同じ data-c-fit-meta-slider-group を PC/モーダルで同期（document よりスコープが明確）
      deps.root.addEventListener('input', function (e) {
        var t = e.target;
        if (!(t instanceof HTMLInputElement)) return;
        if (!t.classList.contains('c-price-filter__range--meta')) return;
        var wrap = t.closest('[data-c-fit-meta-slider-group]');
        var gid = wrap && wrap.getAttribute('data-c-fit-meta-slider-group');
        if (!gid) return;
        deps.syncMetaSliderGroupIn(deps.root, gid, t.value);
        deps.updateSummary();
      });

      var metaGids = new Set();
      deps.sectionQueryAll('.c-price-filter--meta-slider[data-c-fit-meta-slider-group]').forEach(function (wrap) {
        var gid = wrap.getAttribute('data-c-fit-meta-slider-group');
        if (gid) metaGids.add(gid);
      });
      metaGids.forEach(function (gid) {
        var first = deps.root.querySelector('[data-c-fit-meta-slider-group="' + CSS.escape(gid) + '"]');
        var rangeEl = first && first.querySelector('.c-price-filter__range--meta');
        var rv = rangeEl ? rangeEl.value : '0';
        deps.syncMetaSliderGroupIn(deps.root, gid, rv);
      });

      deps.sectionQueryAll('.c-price-filter--meta-slider .c-price-filter__slider-block').forEach(function (sliderBlock) {
        if (typeof ResizeObserver === 'undefined') return;
        var wrap = sliderBlock.closest('.c-price-filter--meta-slider');
        if (!wrap) return;
        var ro = new ResizeObserver(function () {
          var r = wrap.querySelector('.c-price-filter__range--meta');
          if (r) deps.applyMetaSliderIndex(wrap, r.value);
        });
        ro.observe(sliderBlock);
      });
    }

    deps.updateSummary();
    deps.updateCount();

    // 委譲: チェック・ラジオの確定（change）。階層なら親ラジオ同期のうえ要約・件数を更新。セクション配下のみ処理。
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!deps.root || !deps.root.contains(target)) return;

      if (target.type === 'checkbox') {
        cFitSyncHierarchicalParentRadio(target, deps.hierarchicalMerges);
        cFitClearHierarchicalParentIfNoChildren(target, deps.hierarchicalMerges);
      }

      if (target.matches('[data-filter-param]') || target.closest('.c-price-filter')) {
        deps.updateSummary();
        deps.updateCount();
      }
    });

    // 委譲: 価格帯まわりの input イベント（スライダー操作中など change より早い更新用）。要約のみ即時更新。
    document.addEventListener('input', function (e) {
      var target = e.target;
      if (!(target instanceof HTMLInputElement) || !target.closest('.c-price-filter')) return;
      if (!deps.root || !deps.root.contains(target)) return;
      deps.updateSummary();
    });

    deps.sectionQueryAll('.c-filter-content__footer .clear, .c-filter-content__footer .keep').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        var footer = el.closest('.c-filter-content__footer');
        var filterItem = footer && footer.closest('.c-filter-item');
        if (!filterItem) return;

        deps.commitFilterItemSession(filterItem);

        var pcPanel = filterItem.querySelector('.c-filter-content');

        var isClear = el.classList.contains('clear');
        if (isClear) {
          filterItem
            .querySelectorAll('input[type="checkbox"][data-filter-param], input[type="radio"][data-filter-param]')
            .forEach(function (input) {
              input.checked = false;
            });

          var metaCleared = new Set();
          filterItem.querySelectorAll('.c-price-filter--meta-slider').forEach(function (wrap) {
            var gid = wrap.getAttribute('data-c-fit-meta-slider-group');
            if (!gid || metaCleared.has(gid)) return;
            metaCleared.add(gid);
            deps.syncMetaSliderGroupIn(filterItem, gid, '0');
          });
        }

        deps.updateSummary();
        deps.updateCount();

        if (pcPanel) pcPanel.classList.remove('is-active');

        var modal = filterItem.querySelector('modal-content');
        if (modal) modal.open = false;
        deps.setFilterRowAriaExpanded(filterItem, false);
        deps.syncPcFilterRowAria();
      });
    });
  }

  // ==========================================================================
  // セクション単位の初期化（1セクション = 最大1回）
  // window.cFitSearchSectionInit … テーマから直接呼ぶことはほぼなく、末尾の JSON スキャンから呼ばれる。
  // ==========================================================================
  /**
   * ぴったり検索セクション 1 つ分のフィルタ・要約・件数 fetch を束ねて初期化する
   * @param {object} options
   * @param {string} options.sectionId Shopify セクション ID
   * @param {string} [options.searchBasePath] 既定は /search。Liquid の routes.search_url を渡す想定
   * @param {string} [options.countUnavailableFallback] 件数取得失敗時の表示文言
   */
  window.cFitSearchSectionInit = function cFitSearchSectionInit(options) {
    var sectionId = options && options.sectionId;
    if (!sectionId) return;

    var sectionRootEl = document.getElementById('shopify-section-' + sectionId);
    if (sectionRootEl) {
      if (cFitInitializedSectionRoots.has(sectionRootEl)) return;
      cFitInitializedSectionRoots.add(sectionRootEl);
    }

    var C_FIT_FILTER_CONFIG = cFitParseFilterConfigScript(sectionId);

    // searchBasePath … 通常は Liquid の routes.search_url（ストアの検索パス）。defaultQuery は「全商品」を表す固定値。
    var C_FIT = {
      searchBasePath: (options && options.searchBasePath) || '/search',
      defaultQuery: { q: '*', type: 'product' },
      singleParams: new Set(C_FIT_FILTER_CONFIG.singleParamKeys),
    };

    // このセクションのルート（#shopify-section-{id}）。querySelector は常にこの配下に限定して他セクションと混ざらないようにする。
    var C_FIT_SECTION_ROOT = document.getElementById('shopify-section-' + sectionId);
    var C_FIT_COUNT_UNAVAILABLE =
      typeof C_FIT_FILTER_CONFIG.countUnavailable === 'string' && C_FIT_FILTER_CONFIG.countUnavailable.trim() !== ''
        ? C_FIT_FILTER_CONFIG.countUnavailable
        : options.countUnavailableFallback || '';

    var C_FIT_DOM_IDS = {
      selected: 'c-fit-selected-' + sectionId,
      count: 'c-fit-count-' + sectionId,
      submit: 'c-fit-submit-' + sectionId,
    };

    /**
     * セクション内の要素だけ取得（ページに c-search が複数あっても ID が衝突しにくいようスコープを絞る）
     * @param {string} selector
     * @returns {Element[]}
     */
    function cFitSectionQueryAll(selector) {
      return C_FIT_SECTION_ROOT ? Array.from(C_FIT_SECTION_ROOT.querySelectorAll(selector)) : [];
    }

    /**
     * document.getElementById ではなくセクション配下の querySelector（同上）
     * @param {string} id 先頭 # なし
     * @returns {Element|null}
     */
    function cFitGetById(id) {
      return C_FIT_SECTION_ROOT && id ? C_FIT_SECTION_ROOT.querySelector('#' + CSS.escape(id)) : null;
    }

    /**
     * 要約・件数・送信ボタンなど、C_FIT_DOM_IDS で定義した要素を key で取得
     * @param {'selected'|'count'|'submit'} key
     * @returns {Element|null}
     */
    function cFitEl(key) {
      return cFitGetById(C_FIT_DOM_IDS[key]);
    }

    /** 連続 fetch を打ち切るための AbortController（新しい件数取得が始まったら前のリクエストを中止） */
    var cFitCountAbortController = null;
    /** cFitUpdateCount が setTimeout で遅延実行するタイマー ID */
    var cFitCountDebounceTimer = null;
    /** 件数 fetch の結果キャッシュ。キーは pathname+search、値は { n: 件数, t: 取得時刻 ms } */
    var cFitCountResultCache = new Map();
    var cFitUpdateSummary;
    var cFitUpdateCount;
    var cFitUpdateCountImmediate;

    /**
     * 行のトグルボタン aria-expanded（スクリーンリーダー用。PC でパネルが開いているかに合わせる）
     * @param {Element|null} filterItem .c-filter-item
     * @param {boolean} expanded
     */
    function cFitSetFilterRowAriaExpanded(filterItem, expanded) {
      var btn = filterItem && filterItem.querySelector('.c-filter-item__toggle');
      if (btn) btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    /**
     * 全行の aria-expanded を、.c-filter-content.is-active の有無で再計算（PC のみ）
     */
    function cFitSyncPcFilterRowAria() {
      if (!C_FIT_SECTION_ROOT || window.innerWidth < FIT_PC_MIN_WIDTH) return;
      cFitSectionQueryAll('.c-filter-item').forEach(function (item) {
        var panel = item.querySelector('.c-filter-content.is-active');
        cFitSetFilterRowAriaExpanded(item, !!panel);
      });
    }

    // --- ドラフト（行を開いた瞬間のスナップショット。閉じる=破棄、保存/クリア=確定）---
    // PC で別行のパネルを開き直したとき・モーダルを閉じたときに「開く前の状態」へ戻すために使う。
    var cFitFilterDrafts = new WeakMap();

    /**
     * 1フィルタ行の中の、フィルタに関係する input 全部（チェック/ラジオ/スライダー/hidden）
     * @param {Element|null} filterItem
     * @returns {HTMLInputElement[]}
     */
    function cFitGetFilterInputsInItem(filterItem) {
      if (!filterItem) return [];
      return Array.from(filterItem.querySelectorAll('input[data-filter-param], .c-price-filter input'));
    }

    /**
     * 復元時に querySelectorAll の順序に依存しないよう、入力ごとの安定キー
     * @param {HTMLInputElement} input
     * @returns {string}
     */
    function cFitInputStateKey(input) {
      if (!(input instanceof HTMLInputElement)) return '';
      if (input.id) return 'id:' + input.id;
      var param = input.dataset.filterParam || '';
      var nm = input.name || '';
      var type = input.type || 'text';
      if (type === 'checkbox' || type === 'radio') {
        return type + '|' + param + '|' + input.value + '|' + nm;
      }
      var wrap = input.closest('[data-c-fit-meta-slider-group]');
      if (wrap) {
        var gid = wrap.getAttribute('data-c-fit-meta-slider-group') || '';
        if (input.classList.contains('c-price-filter__range--meta')) return 'meta-range|' + gid;
        if (input.classList.contains('c-fit-meta-band-filter-value')) return 'meta-hidden|' + gid;
      }
      return type + '|' + param + '|' + nm + '|' + (input.className || '');
    }

    /**
     * ドラフト保存: 行内の全 input の checked / value をスナップショット
     * @param {Element|null} filterItem
     * @returns {{ key: string, checked: boolean, value: string, type: string }[]}
     */
    function cFitCaptureFilterItemState(filterItem) {
      return cFitGetFilterInputsInItem(filterItem).map(function (input) {
        return {
          key: cFitInputStateKey(input),
          checked: input.checked,
          value: input.value,
          type: input.type,
        };
      });
    }

    /**
     * ドラフト復元: キーで対応する input を探して値を戻し、最後にメタスライダーだけグループ同期
     * @param {Element|null} filterItem
     * @param {{ key: string, checked: boolean, value: string, type: string }[]|undefined} state cFitCaptureFilterItemState の戻り値
     */
    function cFitRestoreFilterItemState(filterItem, state) {
      if (!state || !state.length || !filterItem) return;
      var inputs = cFitGetFilterInputsInItem(filterItem);
      var byKey = new Map();
      for (var i = 0; i < inputs.length; i++) {
        var inp = inputs[i];
        var k = cFitInputStateKey(inp);
        if (k && !byKey.has(k)) byKey.set(k, inp);
      }
      state.forEach(function (s, i) {
        var input = s.key ? byKey.get(s.key) : null;
        if (!input) input = inputs[i];
        if (!input) return;
        if (input.type === 'checkbox' || input.type === 'radio') input.checked = s.checked;
        else input.value = s.value;
      });
      var metaSynced = new Set();
      filterItem.querySelectorAll('.c-price-filter--meta-slider').forEach(function (wrap) {
        var gid = wrap.getAttribute('data-c-fit-meta-slider-group');
        var rangeMeta = wrap.querySelector('.c-price-filter__range--meta');
        if (!gid || !rangeMeta || metaSynced.has(gid)) return;
        metaSynced.add(gid);
        cFitSyncMetaSliderGroupIn(filterItem, gid, rangeMeta.value);
      });
    }

    /**
     * キャンセル: 開いた直後に保存したスナップショットへ戻し、要約と件数を更新
     * @param {Element|null} filterItem
     */
    function cFitRevertFilterItemDraft(filterItem) {
      if (!filterItem) return;
      var state = cFitFilterDrafts.get(filterItem);
      if (!state) return;
      cFitRestoreFilterItemState(filterItem, state);
      cFitFilterDrafts.delete(filterItem);
      cFitUpdateSummary();
      cFitUpdateCount();
    }

    /**
     * パネル/モーダルを開いたとき呼ぶ → 閉じるときにこの時点の状態へ戻せる
     * @param {Element|null} filterItem
     */
    function cFitBeginFilterItemSession(filterItem) {
      if (!filterItem) return;
      cFitFilterDrafts.set(filterItem, cFitCaptureFilterItemState(filterItem));
    }

    /**
     * 保存/クリアで確定したのでドラフトを破棄（以降 revert しない）
     * @param {Element|null} filterItem
     */
    function cFitCommitFilterItemSession(filterItem) {
      if (!filterItem) return;
      cFitFilterDrafts.delete(filterItem);
    }

    /**
     * フィルタ入力の正は常に modal-content（タブレット・PC も SP と同じモーダル UI）
     * @returns {boolean}
     */
    function cFitIsNarrowFitUi() {
      return true;
    }

    /**
     * 価格帯の hidden（メタ値）。表示モードに応じた枝だけ（空は URL に載せない）
     * @returns {HTMLInputElement[]}
     */
    function cFitQueryMetaBandHiddensVisible() {
      if (!C_FIT_SECTION_ROOT) return [];
      var narrow = cFitIsNarrowFitUi();
      var sel = narrow
        ? 'modal-content .c-fit-meta-band-filter-value[data-filter-param]'
        : '.c-filter-item > .c-filter-content .c-fit-meta-band-filter-value[data-filter-param]';
      return Array.from(C_FIT_SECTION_ROOT.querySelectorAll(sel)).filter(function (el) {
        if (!(el instanceof HTMLInputElement)) return false;
        return !!(el.value && el.value.trim() && el.dataset.filterParam);
      });
    }

    /**
     * URL・要約の「正」になるチェック/ラジオだけ取得（価格帯の値は cFitQueryMetaBandHiddensVisible で別途）。
     * narrow のときは modal-content 内のみ、広いときは .c-filter-content 直下のみ。
     * @returns {HTMLInputElement[]}
     */
    function cFitGetFilterInputs() {
      if (!C_FIT_SECTION_ROOT) return [];
      var narrow = cFitIsNarrowFitUi();
      var nodeList = narrow
        ? C_FIT_SECTION_ROOT.querySelectorAll('modal-content input[data-filter-param]')
        : C_FIT_SECTION_ROOT.querySelectorAll('.c-filter-item > .c-filter-content input[data-filter-param]');
      return Array.from(nodeList).filter(function (el) {
        if (!(el instanceof HTMLInputElement)) return false;
        if (el.type !== 'checkbox' && el.type !== 'radio') return false;
        return !!el.dataset.filterParam;
      });
    }

    /**
     * 要約行の表示用: 連続空白を1つにし前後をトリム
     * @param {string} [text]
     * @returns {string}
     */
    function cFitNormalizeText(text) {
      return (text || '').replace(/\s+/g, ' ').trim();
    }

    /**
     * 要約用: label[for] または隣の label、なければ value を返す
     * @param {HTMLInputElement} input
     * @returns {string}
     */
    function cFitLabelForInput(input) {
      var id = input.getAttribute('id');
      if (id && C_FIT_SECTION_ROOT) {
        var label = C_FIT_SECTION_ROOT.querySelector('label[for="' + CSS.escape(id) + '"]');
        if (label) return cFitNormalizeText(label.textContent);
      }
      var nextLabel = input.nextElementSibling;
      if (nextLabel && nextLabel.tagName === 'LABEL') return cFitNormalizeText(nextLabel.textContent);
      return cFitNormalizeText(input.value);
    }

    /**
     * 現在の「見えている」入力から検索 URL を組み立てる（ロジック本体は cFitBuildSearchUrlFromParts）
     * @returns {URL}
     */
    function cFitBuildUrl() {
      return cFitBuildSearchUrlFromParts({
        searchBasePath: C_FIT.searchBasePath,
        defaultQuery: C_FIT.defaultQuery,
        singleParams: C_FIT.singleParams,
        hierarchicalMerges: C_FIT_FILTER_CONFIG.hierarchicalMerges,
        getCheckboxRadios: cFitGetFilterInputs,
        getPriceBandHiddens: cFitQueryMetaBandHiddensVisible,
      });
    }

    /**
     * 要約（#c-fit-selected-*）と「商品をさがす」リンクの href を更新。
     * 階層フィルタは子ラベルのみ表示（親だけ選ばれた場合は親ラベル）。重複は Set でまとめる。
     */
    cFitUpdateSummary = function cFitUpdateSummaryImpl() {
      var selectedEl = cFitEl('selected');
      var submitEl = cFitEl('submit');
      if (!selectedEl || !submitEl) return;

      var inputs = cFitGetFilterInputs().filter(function (i) {
        return i.checked;
      });
      var labels = [];
      var seen = new Set();
      /**
       * 要約ラベルを重複なく labels に追加（内部ヘルパ）
       * @param {string} text
       */
      function cFitAddSummaryLabel(text) {
        var n = cFitNormalizeText(text);
        if (!n || seen.has(n)) return;
        seen.add(n);
        labels.push(n);
      }

      var hMergesSummary = C_FIT_FILTER_CONFIG.hierarchicalMerges || [];
      /**
       * この data-filter-param が階層の「子」側なら merge 定義を返す
       * @param {string} param
       * @returns {object|undefined}
       */
      function cFitMergeForChildParam(param) {
        return hMergesSummary.find(function (m) {
          return m && m.childParam === param;
        });
      }
      /**
       * 要約で「親だけ」後段処理するため、この param が階層の親キーか判定
       * @param {string} param
       * @returns {boolean}
       */
      function cFitIsParentParamSummary(param) {
        return hMergesSummary.some(function (m) {
          return m && m.parentParam === param;
        });
      }
      /**
       * この data-filter-param が階層の「親」側なら merge 定義を返す
       * @param {string} param
       * @returns {object|undefined}
       */
      function cFitMergeForParentParam(param) {
        return hMergesSummary.find(function (m) {
          return m && m.parentParam === param;
        });
      }
      /**
       * 階層 merge で子チェックが1つでも ON か
       * @param {object|undefined} merge
       * @returns {boolean}
       */
      function cFitHasCheckedChildForMerge(merge) {
        if (!merge || !merge.childParam) return false;
        for (var k = 0; k < inputs.length; k++) {
          if (inputs[k].dataset.filterParam === merge.childParam && inputs[k].checked) {
            return true;
          }
        }
        return false;
      }

      // 1周目: 子チェックは子ラベルのみ、単独 param はラベルのみ。親 param 単体はまだ出さない
      for (var i = 0; i < inputs.length; i++) {
        var input = inputs[i];
        var param = input.dataset.filterParam;
        if (!param) continue;
        var merge = cFitMergeForChildParam(param);
        if (merge) {
          cFitAddSummaryLabel(cFitLabelForInput(input));
          continue;
        }
        if (cFitIsParentParamSummary(param)) continue;
        cFitAddSummaryLabel(cFitLabelForInput(input));
      }
      // 2周目: 親ラジオだけ単独で選ばれたケース（子なし）を要約に足す
      for (var j = 0; j < inputs.length; j++) {
        var inp2 = inputs[j];
        var p2 = inp2.dataset.filterParam;
        if (!cFitIsParentParamSummary(p2)) continue;
        var parentMerge = cFitMergeForParentParam(p2);
        if (parentMerge && cFitHasCheckedChildForMerge(parentMerge)) continue;
        cFitAddSummaryLabel(cFitLabelForInput(inp2));
      }

      // 価格帯は hidden のラベル（dataset.cFitMetaBandLabel か value）
      cFitQueryMetaBandHiddensVisible().forEach(function (h) {
        var lab = h.dataset.cFitMetaBandLabel || (h.value || '').trim();
        cFitAddSummaryLabel(lab);
      });

      selectedEl.textContent = labels.join('、');

      var url = cFitBuildUrl();
      submitEl.setAttribute('href', url.pathname + url.search);
    };

    /**
     * ① ?section_id=c-fit-count-preview で軽量 HTML を取得（パース負荷・転送量を抑える）
     * ② 件数が取れなければ同じクエリでフル検索ページを取りにいく
     * ③ それでもダメなら翻訳文字列 countUnavailable を表示
     * @returns {Promise<void>}
     */
    cFitUpdateCountImmediate = async function cFitUpdateCountImmediateImpl() {
      var countEl = cFitEl('count');
      if (!countEl) return;

      var baseUrl = cFitBuildUrl();
      var cacheKey = baseUrl.pathname + baseUrl.search;
      var cached = cFitCountResultCache.get(cacheKey);
      // 同じ条件を何度も叩かない（フィルタ操作中の連打対策）
      if (cached && Date.now() - cached.t < FIT_COUNT_CACHE_TTL_MS) {
        countEl.textContent = cached.n + '件';
        return;
      }

      if (cFitCountAbortController) {
        try {
          cFitCountAbortController.abort();
        } catch (_) {}
      }
      cFitCountAbortController = new AbortController();
      var signal = cFitCountAbortController.signal;

      try {
        var url = cFitBuildUrl();
        url.searchParams.set('section_id', 'c-fit-count-preview');
        var res = await cFitFetchHtml(url.pathname + url.search, signal);
        var html = res.ok ? await res.text() : '';
        var n = html ? cFitParseSearchProductCount(html) : null;
        // 軽量 section が無効・404・想定外 HTML のときは、フィルタ付き通常検索へフォールバック
        if (n === null || n === undefined) {
          url = cFitBuildUrl();
          res = await cFitFetchHtml(url.pathname + url.search, signal);
          if (!res.ok) {
            countEl.textContent = C_FIT_COUNT_UNAVAILABLE;
            return;
          }
          html = await res.text();
          n = cFitParseSearchProductCount(html);
        }
        if (n !== null && n !== undefined) {
          countEl.textContent = n + '件';
          cFitCountResultCache.set(cacheKey, { n: n, t: Date.now() });
        } else {
          countEl.textContent = C_FIT_COUNT_UNAVAILABLE;
        }
      } catch (e) {
        // 中止はユーザーがすぐ別操作した場合などなので黙って終了
        if (e.name === 'AbortError') return;
        countEl.textContent = C_FIT_COUNT_UNAVAILABLE;
      }
    };

    /**
     * 入力のたびに即 fetch しないようデバウンスして cFitUpdateCountImmediate を呼ぶ
     */
    cFitUpdateCount = function cFitUpdateCountImpl() {
      if (!C_FIT_SECTION_ROOT) return;
      clearTimeout(cFitCountDebounceTimer);
      cFitCountDebounceTimer = setTimeout(function () {
        cFitCountDebounceTimer = null;
        void cFitUpdateCountImmediate();
      }, FIT_COUNT_DEBOUNCE_MS);
    };

    var cFitSectionDeps = {
      FIT_PC_MIN_WIDTH: FIT_PC_MIN_WIDTH,
      FIT_NARROW_MQL: FIT_NARROW_MQL,
      root: C_FIT_SECTION_ROOT,
      sectionQueryAll: cFitSectionQueryAll,
      revertFilterItemDraft: cFitRevertFilterItemDraft,
      beginFilterItemSession: cFitBeginFilterItemSession,
      commitFilterItemSession: cFitCommitFilterItemSession,
      setFilterRowAriaExpanded: cFitSetFilterRowAriaExpanded,
      syncPcFilterRowAria: cFitSyncPcFilterRowAria,
      syncMetaSliderGroupIn: cFitSyncMetaSliderGroupIn,
      applyMetaSliderIndex: cFitApplyMetaSliderIndex,
      hierarchicalMerges: C_FIT_FILTER_CONFIG.hierarchicalMerges || [],
      updateSummary: function () {
        cFitUpdateSummary();
      },
      updateCount: function () {
        cFitUpdateCount();
      },
    };

    cFitRegisterSectionChrome(cFitSectionDeps);
    cFitDomReady(function () {
      cFitRegisterSectionFilterEvents(cFitSectionDeps);
    });
  };

  // ============================================================================
  // エントリポイント（ページ全体）
  // ============================================================================
  /**
   * c-search.liquid が出力した #c-fit-filter-config-*（application/json）を走査し、sectionId があるものだけ cFitSearchSectionInit する
   */
  cFitDomReady(function () {
    document.querySelectorAll('script[type="application/json"][id^="c-fit-filter-config-"]').forEach(function (el) {
      try {
        var cfg = JSON.parse(el.textContent || '{}');
        if (!cfg.sectionId || !cfg.searchBasePath) return;
        window.cFitSearchSectionInit({
          sectionId: String(cfg.sectionId),
          searchBasePath: cfg.searchBasePath,
          countUnavailableFallback: typeof cfg.countUnavailable === 'string' ? cfg.countUnavailable : '',
        });
      } catch (_) {}
    });
  });
})();
